type FocusObject = { id: string; className: string; readonly parent?: FocusObject; readonly interactivCreated?: boolean };

/** Children of a manually placed group share the group's independent focus boundary. */
export function independentEloOwner(object: FocusObject | undefined): FocusObject | undefined {
  const seen = new Set<FocusObject>();
  for (let candidate = object; candidate && !seen.has(candidate); candidate = candidate.parent) {
    if (candidate.interactivCreated) return candidate;
    seen.add(candidate);
  }
  return undefined;
}
type FocusState = {
  readonly selection?: FocusObject;
  editMode: { value: string };
  floorImageRenderOptions: { value: unknown | null };
  hoveredEloOwner?: { value: FocusObject | undefined };
  eloSelectionFocus?: FocusObject;
};

/** Electrical children can retain an existing owner focus, but never create one. */
export function nextEloSelectionFocus<T extends FocusObject>(selection: T | undefined, previous: T | undefined): T | undefined {
  if (!selection || selection.className === "C1_Room") return undefined;
  if (["C1_Furniture", "C1_Opening"].includes(selection.className)) return selection;
  let owner: FocusObject | undefined = selection;
  const seen = new Set<FocusObject>();
  while (owner && ["C1_EloItem", "C1_EloGroup", "C1_Consumer", "C1_Button"].includes(owner.className) && !seen.has(owner)) {
    if (owner.interactivCreated) return undefined;
    seen.add(owner);
    owner = owner.parent;
  }
  return previous && previous.className !== "C1_Room" && owner?.id === previous.id && owner?.className === previous.className ? previous : undefined;
}

/** Dim unrelated objects once at their owner layer, never each nested ELO symbol. */
export function eloSelectionOpacity(object: FocusObject | undefined, state: FocusState): 1 | 0.575 {
  const focus = nextEloSelectionFocus(state.selection, state.eloSelectionFocus);
  if (state.floorImageRenderOptions.value !== null ||
    !["EloConcept", "EloDetail", "interactionConcept"].includes(state.editMode.value) ||
    !focus || !["C1_Furniture", "C1_Opening"].includes(focus.className)) return 1;
  const hovered = state.hoveredEloOwner?.value;
  if (object && hovered && object.id === hovered.id && object.className === hovered.className) return 1;
  const visited = new Set<FocusObject>();
  for (let candidate = object; candidate && !visited.has(candidate); candidate = candidate.parent) {
    if (candidate === focus || (candidate.id === focus.id && candidate.className === focus.className)) return 1;
    visited.add(candidate);
  }
  return 0.575;
}

/** Share the owner's hover between its drawing and the separate ELO symbol layer. */
export function eloSelectionHoverHandlers(object: FocusObject, state: FocusState) {
  return {
    onPointerEnter: () => {
      if (state.hoveredEloOwner && eloSelectionOpacity(object, state) !== 1) state.hoveredEloOwner.value = object;
    },
    onPointerLeave: () => {
      if (state.hoveredEloOwner?.value === object) state.hoveredEloOwner.value = undefined;
    },
  };
}
